import cn from "classnames";

import { useCallback, useEffect, useMemo, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlightIcon from "@mui/icons-material/Flight";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";

import ProductInfo from "@/components/FlightItem/ProductInfo";
import SummaryInfo from "@/components/FlightItem/SummaryInfo";
import { ListViewType } from "@/constants";
import { AIRPORT_MAP } from "@/constants/airport";
import { NEED_BACKGROUND_PROGRAM, airlineBgColor } from "@/constants/config";
import { siteName } from "@/constants/config";
import {
  BANK_GUIDE_MAP,
  BANK_IMG,
  BANK_NAME_MAP,
  BANK_TRANSFER_LINK,
  BUY_POINT_LINKS,
  PROGRAM_BUY_LINK,
  PROGRAM_IMG,
} from "@/constants/programs";
import usePlatform from "@/hooks/usePlatform";
import { FlightItem as FlightItemType } from "@/typings";
import { calculateTargetUrl, formatPts } from "@/utils/index";

import CabinColorItem from "./CabinColorItem";

export interface OptionDialogProps {
  data?: FlightItemType;
  onClose: () => void;
  viewType?: number;
}

const Title = ({ index, text }: { index: number; text: string }) => {
  return (
    <Typography className="!text-[18px] !font-black	flex items-center">
      <Typography
        className="w-[18px] h-[18px] !text-[12px] !leading-[18px] !font-black !mr-[5px] text-center rounded-[50%]"
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: (theme) => theme.palette.background.default,
        }}
      >
        {index}
      </Typography>
      {text}
    </Typography>
  );
};
const OptionTitle = ({ text }: { text: string }) => {
  return (
    <Typography className="!text-[15px] !font-black	flex items-center !mt-[16px]">
      <Typography
        className="w-[18px] h-[18px] !text-[12px] !leading-[18px] !font-black !mr-[5px] text-center rounded-[0%] "
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: (theme) => theme.palette.background.default,
        }}
      >
        |
      </Typography>
      {text}
    </Typography>
  );
};

export default function OptionDialog({ data, viewType, onClose }: OptionDialogProps) {
  const products = useMemo(() => data?.fare?.products || [], [data]);
  const origin_airport = products[0]?.origin;
  const arrival_airport = products[products.length - 1]?.destination;
  const departure_date = data?.date;
  const program_code = data?.program_code;
  const cabin = data?.payment?.cabin || data?.final_cabin_type;

  // console.log("%c [  ]-87", "font-size:13px; background:pink; color:#bf2c9f;", data);
  // Convert time to date format
  const buy_ticket_url = calculateTargetUrl(
    program_code,
    origin_airport,
    arrival_airport,
    departure_date,
    cabin
  );
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { isMobile } = usePlatform();
  const isVertical = useMediaQuery("(max-width:880px)");

  const airportDisplay = (airport_code: string) => {
    return `${AIRPORT_MAP[airport_code].city}`;
  };

  const options = useMemo(() => {
    return (data?.transfer_options || []).map((ele) => ({
      img: BANK_IMG[ele.program],
      bank_name: ele.program,
      transfer_link: BANK_TRANSFER_LINK[ele.program],
      title: BANK_NAME_MAP[ele.program],
      points: ele.points,
      guide_link: BANK_GUIDE_MAP[ele.program],
      bonus: ele.bonus,
    }));
  }, [data]);

  const onSummaryClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setExpanded(!expanded);
    },
    [expanded]
  );

  useEffect(() => {
    if (data) {
      setOpen(true);
    } else {
      setOpen(false);
      setExpanded(false);
    }
  }, [data]);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      maxWidth={false}
      className="!max-w-5xl mx-auto"
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          width: "100%",
          borderRadius: "12px",
          maxWidth: "calc(100% - 20px)",
        },
      }}
    >
      <IconButton onClick={onClose} className="!absolute top-[10px] right-[10px] z-[10] ">
        <CloseIcon fontSize="small" />
      </IconButton>

      {data ? (
        <DialogContent
          className={cn("relative w-full !py-[20px]", isMobile ? "!px-[15px]" : "!px-[20px]")}
        >
          <Box className={cn("flex items-center mb-[20px]", isVertical ? "flex-col" : "")}>
            <Typography className="!font-black !text-[18px]">
              {airportDisplay(data.fare.products[0].origin)}
            </Typography>
            <FlightIcon
              className={cn(
                "mx-[10px] my-[2px] !text-[16px]",
                isVertical ? "rotate-180" : "rotate-90"
              )}
            />
            <Typography className={cn("!font-black", isVertical ? "!text-[17px]" : "!text-[20px]")}>
              {airportDisplay(data.fare.products[data.fare.products.length - 1].destination)}
            </Typography>
          </Box>

          <Grid container columnSpacing={4}>
            <Grid item xs={12} sm={5}>
              <div className="mb-[15px]">
                <Title index={1} text="Review your flight details" />
              </div>

              <Accordion expanded={isMobile ? expanded : true} sx={{ boxShadow: "2" }}>
                <AccordionSummary
                  className="!px-[10px]"
                  onClick={onSummaryClick}
                  expandIcon={
                    isMobile ? (
                      <IconButton>
                        <ExpandMoreIcon />
                      </IconButton>
                    ) : null
                  }
                >
                  <SummaryInfo
                    data={data}
                    showDetail={isMobile}
                    showCabin={viewType !== ListViewType.Grid}
                  />
                </AccordionSummary>

                <AccordionDetails
                  className="!pt-[15px] !pb-[20px]"
                  sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
                >
                  {products.map((product, i) => (
                    <ProductInfo
                      key={i}
                      data={data}
                      product={product}
                      isLast={i === products.length - 1}
                      showCabin={viewType !== ListViewType.Grid}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} sm={7}>
              {isMobile ? <div className="mt-[30px]" /> : null}

              <Title index={2} text="Select a way to book" />

              <Typography variant="body2" className="!font-black py-[10px] pl-[23px]">
                {"Prices are for 1 adult passenger. "}
                <Typography variant="body2" className="!inline">
                  Additional bag fees and other fees may apply.
                </Typography>
              </Typography>
              <Typography variant="body2" className="py-[0px] pl-[23px]">
                Tip: Cross-check the flight from the app in case it’s not showing on the website.
              </Typography>

              <>
                <OptionTitle text="Book Now With Airline" />
                <Grid
                  key={89}
                  container
                  className="py-[15px]"
                  sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                >
                  <Grid item xs={isVertical ? 3.5 : 4} className="flex items-center">
                    <Box
                      className={cn("flex items-center justify-center", {
                        "flex-col": isVertical,
                      })}
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? airlineBgColor : "transparent",
                        borderRadius: "4px",
                        padding: "8px",
                        width: "100px",
                        height: "40px",
                      }}
                    >
                      <img className="w-[80px]" src={PROGRAM_IMG[data.program_code]} />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={isVertical ? 5 : 4}
                    className="w-full flex !flex-col justify-center items-center space-y-[5px]"
                  >
                    {viewType === ListViewType.Grid ? (
                      <>
                        {Object.keys(data.cabin_prices).map((item) => (
                          <CabinColorItem key={item} data={data} type={item} />
                        ))}
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" className="!font-black">
                          {formatPts(data.award_points)}
                        </Typography>

                        <Typography variant="body2" color="textSecondary" mt={"2px"}>
                          {data.surcharge > 0 && `+$${data.surcharge}`}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={isVertical ? 3.5 : 4} className="flex justify-end items-center">
                    <Button
                      href={buy_ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="small"
                      sx={{ minWidth: "90px", minHeight: "28px", borderRadius: "12px" }}
                    >
                      Book Now
                    </Button>
                  </Grid>
                </Grid>
              </>

              {options.length > 0 && <OptionTitle text="Transfer Points" />}
              {options.map((option, i) => {
                return (
                  <Grid
                    key={i}
                    container
                    className="py-[15px]"
                    sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                    gap={2}
                  >
                    <Grid
                      item
                      xs
                      container
                      direction="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Grid item>
                        <Box
                          className={cn("flex items-center justify-center", {
                            "flex-col": isVertical,
                          })}
                        >
                          <Box
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? airlineBgColor : "transparent",
                              borderRadius: "4px",
                              padding: "4px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100px",
                              height: "40px",
                            }}
                          >
                            <img className="w-[80px]" src={option.img} />
                          </Box>

                          <ArrowForwardIcon
                            className={cn("mx-[10px] my-[5px] !text-[13px]", {
                              "rotate-90": isVertical,
                            })}
                            sx={{ color: (theme) => theme.palette.text.secondary }}
                          />

                          <Box
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? airlineBgColor : "transparent",
                              borderRadius: "4px",
                              padding: "4px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100px",
                              height: "40px",
                            }}
                          >
                            <img className="w-[80px]" src={PROGRAM_IMG[data.program_code]} />
                          </Box>
                        </Box>
                      </Grid>

                      {option.bonus && (
                        <Grid
                          item
                          sx={{
                            mt: 2,
                            border: (theme) => `1px solid ${theme.palette.primary.main}`,
                            borderRadius: "12px",
                            padding: "2px 10px",
                          }}
                        >
                          <Typography color="primary">
                            {option.bonus ? `+${option.bonus}% transfer bonus` : null}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Grid
                      item
                      xs
                      className="w-full flex !flex-col justify-center items-center space-y-[5px]"
                    >
                      {viewType === ListViewType.Grid ? (
                        <>
                          {Object.keys(data.cabin_prices)
                            .sort((a, b) => {
                              const order = {
                                Economy: 1,
                                "Premium Economy": 2,
                                Business: 3,
                                First: 4,
                              };
                              return (order[a] || 99) - (order[b] || 99);
                            })
                            .map((item) => (
                              <CabinColorItem
                                key={item}
                                data={data}
                                type={item}
                                bank_name={option.bank_name}
                              />
                            ))}
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" className="!font-black">
                            {formatPts(option.points)}
                          </Typography>

                          <Typography variant="body2" color="textSecondary" mt={"2px"}>
                            {data.surcharge > 0 && `+$${data.surcharge}`}
                          </Typography>
                        </>
                      )}
                    </Grid>

                    <Grid item xs={isVertical ? 4 : 3} className="flex justify-end items-center">
                      <div className="flex flex-col">
                        <Button
                          href={option.transfer_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          size="small"
                          sx={{ minWidth: "90px", minHeight: "28px", borderRadius: "6px" }}
                        >
                          Select Deal
                        </Button>
                        <Button
                          href={"https://www." + siteName + ".com" + option.guide_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          // variant="contained"
                          size="small"
                          className="mt-[100px]"
                        >
                          {isMobile ? "Transfer Guide" : "Transfer Guide"}
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                );
              })}
              {BUY_POINT_LINKS[data.program_code.toLocaleLowerCase()] && (
                <>
                  <OptionTitle text="Buy Points" />
                  <Grid
                    key={89}
                    container
                    className="py-[15px]"
                    sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                  >
                    <Grid item xs={isVertical ? 3.5 : 4} className="flex items-center">
                      <Box
                        className={cn("flex items-center justify-center", {
                          "flex-col": isVertical,
                        })}
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" && true ? airlineBgColor : "transparent",
                          borderRadius: "3px",
                          padding: "4px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100px",
                          height: "40px",
                        }}
                      >
                        <img className="w-[80px]" src={PROGRAM_IMG[data.program_code]} />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isVertical ? 5 : 4}
                      className="w-full flex !flex-col justify-center items-center space-y-[5px]"
                    >
                      {viewType === ListViewType.Grid ? (
                        <>
                          {Object.keys(data.cabin_prices)
                            .sort((a, b) => {
                              const order = {
                                Economy: 1,
                                "Premium Economy": 2,
                                Business: 3,
                                First: 4,
                              };
                              return (order[a] || 99) - (order[b] || 99);
                            })
                            .map((item) => (
                              <CabinColorItem key={item} data={data} type={item} />
                            ))}
                        </>
                      ) : (
                        <Typography variant="body2" className="!font-black">
                          {formatPts(data.award_points)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={isVertical ? 3.5 : 4} className="flex justify-end items-center">
                      <Button
                        href={BUY_POINT_LINKS[data.program_code.toLocaleLowerCase()]}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        size="small"
                        sx={{ minWidth: "90px", minHeight: "28px", borderRadius: "7px" }}
                      >
                        Select Deal
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
